import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Multiselect } from '@/components/ui/multiselect';
import {
  useBackOfficeWorkspacesService,
  useUpdateUserWorkspacesService,
} from '@/services/backoffice';
import { X } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { toast } from 'sonner';

interface UserWorkspaceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    workspaceIds?: string[];
  } | null;
}

export const UserWorkspaceModal: FC<UserWorkspaceModalProps> = ({
  open,
  onOpenChange,
  user,
}) => {
  const { workspaces } = useBackOfficeWorkspacesService();
  const { updateUserWorkspaces, isPending } = useUpdateUserWorkspacesService();

  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>(
    []
  );

  // Initialize selected workspaces when user changes
  useMemo(() => {
    if (user?.workspaceIds) {
      setSelectedWorkspaceIds(user.workspaceIds);
    } else {
      setSelectedWorkspaceIds([]);
    }
  }, [user]);

  // Transform workspaces for multiselect
  const workspaceOptions = useMemo(
    () =>
      workspaces.map(workspace => ({
        value: workspace.id,
        label: `${workspace.name} (${workspace.uname})`,
      })),
    [workspaces]
  );

  // Get selected workspace details for tags
  const selectedWorkspaces = useMemo(() => {
    return workspaces.filter(workspace =>
      selectedWorkspaceIds.includes(workspace.id)
    );
  }, [workspaces, selectedWorkspaceIds]);

  const handleSave = () => {
    if (!user) return;

    updateUserWorkspaces(
      {
        userId: user.id,
        workspaceIds: selectedWorkspaceIds,
      },
      {
        onSuccess: () => {
          toast.success('Workspace assignments updated successfully');
          onOpenChange(false);
        },
        onError: (error: any) => {
          const errorMessage =
            error?.message ||
            error?.response?.body?.message ||
            error?.response?.data?.message ||
            'Failed to update workspace assignments';
          toast.error(errorMessage);
        },
      }
    );
  };

  const removeWorkspace = (workspaceId: string) => {
    setSelectedWorkspaceIds(prev => prev.filter(id => id !== workspaceId));
  };

  // Custom trigger with tags
  const customTrigger = (
    <div className="min-h-[40px] w-full border rounded-md px-3 py-2 bg-background">
      {selectedWorkspaces.length === 0 ? (
        <span className="text-muted-foreground">Select workspaces...</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {selectedWorkspaces.map(workspace => (
            <div
              key={workspace.id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-secondary text-secondary-foreground rounded-md text-sm"
            >
              <span className="font-mono">{workspace.uname}</span>
              <button
                type="button"
                onPointerDown={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeWorkspace(workspace.id);
                }}
                className="hover:bg-secondary-foreground/20 rounded-sm p-0.5"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {`Manage Workspaces for ${user.firstName} ${user.lastName}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Workspaces</label>
            <Multiselect
              options={workspaceOptions}
              selected={selectedWorkspaceIds}
              onChange={setSelectedWorkspaceIds}
              placeholder="Select workspaces..."
              showSearch
              showClearAll
              contentClassName="max-h-[300px] overflow-y-auto w-full"
              trigger={customTrigger}
            />
            <p className="text-xs text-muted-foreground">
              {selectedWorkspaceIds.length === 0
                ? 'No workspaces selected'
                : selectedWorkspaceIds.length === 1
                  ? `${selectedWorkspaceIds.length} workspace selected`
                  : `${selectedWorkspaceIds.length} workspaces selected`}
            </p>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
